// Authentication utilities

const TOKEN_COOKIE_NAME = 'auth_token';
const TOKEN_EXPIRY_HOURS = 12;

/**
 * Set cookie with expiration
 */
export const setCookie = (name, value, hours) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + hours * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

/**
 * Get cookie value by name
 */
export const getCookie = (name) => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Delete cookie
 */
export const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

/**
 * Generate a simple token (in production, use JWT or similar)
 */
export const generateToken = () => {
  const token = btoa(
    JSON.stringify({
      timestamp: Date.now(),
      random: Math.random().toString(36).substring(2, 15),
    })
  );
  return token;
};

/**
 * Check if token is valid (not expired)
 */
export const isTokenValid = (token) => {
  try {
    const decoded = JSON.parse(atob(token));
    const tokenAge = Date.now() - decoded.timestamp;
    const maxAge = TOKEN_EXPIRY_HOURS * 60 * 60 * 1000; // 12 hours in milliseconds
    return tokenAge < maxAge;
  } catch (error) {
    return false;
  }
};

/**
 * Login function - validates password and sets token
 */
export const login = async (password) => {
  try {
    // Fetch password from password.txt
    const response = await fetch('/password.txt');
    if (!response.ok) {
      throw new Error('Failed to fetch password');
    }
    const correctPassword = (await response.text()).trim();

    if (password === correctPassword) {
      const token = generateToken();
      setCookie(TOKEN_COOKIE_NAME, token, TOKEN_EXPIRY_HOURS);
      return { success: true };
    } else {
      return { success: false, error: 'Invalid password' };
    }
  } catch (error) {
    console.error('Login error:', error);
    return { success: false, error: 'An error occurred during login' };
  }
};

/**
 * Logout function - removes token
 */
export const logout = () => {
  deleteCookie(TOKEN_COOKIE_NAME);
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  const token = getCookie(TOKEN_COOKIE_NAME);
  if (!token) {
    return false;
  }
  
  const isValid = isTokenValid(token);
  // Clean up expired token
  if (!isValid) {
    deleteCookie(TOKEN_COOKIE_NAME);
  }
  
  return isValid;
};

